import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'next-i18next';
import { type z } from 'zod';
import { Select, type SelectProps } from '@/components/select';
import { ALL_GRADES_NUMBER } from '@/views/build/util';
import { useSignUpSchoolGrade } from '../hook';
import { type StudentSchema, type TeacherSchema } from '../schema';
import { useGetSchoolTypeOptions } from './selectOptions';
import { WithLabel } from './WithLabel';

/**
 * 회원가입 폼에 들어가는 Select 컴포넌트 모음
 * 학교급 선택, 학년 선택, 이용 목적 선택
 */

// 학교급 선택
export const SignSelectSchoolType = (props: Omit<SelectProps, 'options'>) => {
  const {
    value,
    onValueChange,
    ...restProps
  } = props;
  const schoolTypeOptions = useGetSchoolTypeOptions();
  return <Select value={value} onValueChange={onValueChange} options={schoolTypeOptions} {...restProps} className="h-[47px] w-full rounded-sm border-[1px]" data-sentry-element="Select" data-sentry-component="SignSelectSchoolType" data-sentry-source-file="SignSelect.tsx" />;
};

// 학년 선택
export const SignSelectSchoolGrade = (props: Omit<SelectProps, 'options'>) => {
  const {
    onValueChange,
    ...restProps
  } = props;
  const {
    control
  } = useFormContext<z.infer<typeof TeacherSchema | typeof StudentSchema>>();
  const schoolType = useWatch({
    control,
    name: 'schoolType'
  });
  const value = useWatch({
    control,
    name: 'schoolGrade'
  });
  const {
    t
  } = useTranslation(['common']);
  const grades = useSignUpSchoolGrade();
  const noGrade = schoolType === 'COLLEGE' || schoolType === 'OTHER' || schoolType === 'KINDERGARTEN';
  const gradeOptions = useMemo(() => [...grades.filter(grade => grade !== ALL_GRADES_NUMBER).map(grade => ({
    label: t('common:school.info.nGrade', {
      n: grade.toString()
    }),
    value: grade.toString()
  })), ...(noGrade ? [{
    label: t('signup.school.noGrade'),
    value: '0'
  }] : [])], [grades, noGrade, t]);
  return <Select placeHolder={t('signup.school.gradePlaceholder')} value={value} defaultValue={noGrade ? '0' : ''} onValueChange={onValueChange} disabled={noGrade} options={gradeOptions} {...restProps} className="h-[47px] w-full rounded-sm border-[1px]" data-sentry-element="Select" data-sentry-component="SignSelectSchoolGrade" data-sentry-source-file="SignSelect.tsx" />;
};

// 이용 목적 선택
export const SignSelectUsagePurpose = (props: SelectProps) => {
  const {
    t
  } = useTranslation(['common']);
  const errorMessage = props.isError ? t('account.signup.requiredField') : undefined;
  return <WithLabel label={t('account.signup.purposeUse')} errorMessage={errorMessage} data-sentry-element="WithLabel" data-sentry-component="SignSelectUsagePurpose" data-sentry-source-file="SignSelect.tsx">
      <Select {...props} placeHolder={t('account.signup.selectPurposeUse')} isError={!!errorMessage} options={props.options} className={`border-input-border ${props.isError ? 'border-[#FFACB6] focus-within:border-[#FFACB6]' : ''}`} data-sentry-element="Select" data-sentry-source-file="SignSelect.tsx" />
    </WithLabel>;
};